<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  cleanLabelTitle,
  groupLabelsByCustomAttribute,
} from 'dashboard/helper/commons.js';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const selectedLabel = ref({});
const deleteDialogRef = ref(null);
const deleteMode = ref('remove');
const replacementLabelId = ref('');

const records = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);
const contactAttributes = computed(
  () => getters['attributes/getContactAttributes'].value
);

const deleteMessage = computed(() =>
  t('LABEL_MGMT.DELETE.DIALOG.DESCRIPTION', {
    label: cleanLabelTitle(selectedLabel.value),
  })
);
const replacementGroups = computed(() => {
  const availableLabels = records.value.filter(
    label => label.id !== selectedLabel.value.id
  );
  return groupLabelsByCustomAttribute(
    availableLabels,
    contactAttributes.value
  );
});
const isDeleteDisabled = computed(
  () => deleteMode.value === 'move' && !replacementLabelId.value
);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  selectedLabel.value = response;
  deleteMode.value = 'remove';
  replacementLabelId.value = '';
  deleteDialogRef.value?.open();
};
const closeDeletePopup = () => {
  deleteDialogRef.value?.close();
};

const deleteLabel = async () => {
  const id = selectedLabel.value.id;
  try {
    await store.dispatch('labels/delete', {
      id,
      replacementLabelId:
        deleteMode.value === 'move' ? replacementLabelId.value : undefined,
    });
    await store.dispatch('attributes/get');
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    closeDeletePopup();
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  deleteLabel();
};

const tableHeaders = computed(() => {
  return [
    t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
  ];
});

onBeforeMount(() => {
  store.dispatch('labels/get');
  store.dispatch('attributes/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LABEL_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('LABEL_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('LABEL_MGMT.HEADER')"
        :description="$t('LABEL_MGMT.DESCRIPTION')"
        :link-text="$t('LABEL_MGMT.LEARN_MORE')"
        feature-name="labels"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="label in records" :key="label.title">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ cleanLabelTitle(label) }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">{{ label.description }}</td>
            <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center">
                <span
                  class="w-4 h-4 mr-1 border border-solid rounded rtl:mr-0 rtl:ml-1 border-n-weak"
                  :style="{ backgroundColor: label.color }"
                />
                {{ label.color }}
              </div>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[label.id]"
                  @click="openEditPopup(label)"
                />
                <Button
                  v-if="!label.managed"
                  v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[label.id]"
                  @click="openDeletePopup(label)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('LABEL_MGMT.DELETE.DIALOG.TITLE')"
      :description="deleteMessage"
      :confirm-button-label="$t('LABEL_MGMT.DELETE.DIALOG.CONFIRM')"
      :cancel-button-label="$t('LABEL_MGMT.DELETE.DIALOG.CANCEL')"
      :disable-confirm-button="isDeleteDisabled"
      :is-loading="loading[selectedLabel.id]"
      @confirm="confirmDeletion"
    >
      <div class="flex flex-col gap-3">
        <label
          class="flex gap-3 p-3 border rounded-lg cursor-pointer border-n-weak"
        >
          <input v-model="deleteMode" type="radio" value="remove" />
          <span class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('LABEL_MGMT.DELETE.DIALOG.REMOVE.TITLE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('LABEL_MGMT.DELETE.DIALOG.REMOVE.DESCRIPTION') }}
            </span>
          </span>
        </label>
        <label
          class="flex gap-3 p-3 border rounded-lg cursor-pointer border-n-weak"
        >
          <input v-model="deleteMode" type="radio" value="move" />
          <span class="flex flex-col flex-1 gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('LABEL_MGMT.DELETE.DIALOG.MOVE.TITLE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('LABEL_MGMT.DELETE.DIALOG.MOVE.DESCRIPTION') }}
            </span>
          </span>
        </label>
        <div v-if="deleteMode === 'move'" class="flex flex-col gap-2">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="replacement-label"
          >
            {{ $t('LABEL_MGMT.DELETE.DIALOG.MOVE.SELECT_LABEL') }}
          </label>
          <select
            id="replacement-label"
            v-model="replacementLabelId"
            class="w-full rounded-lg border-n-weak bg-n-alpha-1 text-sm text-n-slate-12"
          >
            <option value="" disabled>
              {{ $t('LABEL_MGMT.DELETE.DIALOG.MOVE.PLACEHOLDER') }}
            </option>
            <optgroup
              v-for="group in replacementGroups.groups"
              :key="group.id"
              :label="group.name"
            >
              <option
                v-for="label in group.labels"
                :key="label.id"
                :value="label.id"
              >
                {{ cleanLabelTitle(label) }}
              </option>
            </optgroup>
            <optgroup
              v-if="replacementGroups.otherLabels.length"
              :label="$t('CONTACT_PANEL.LABELS.CONTACT.OTHERS_HEADER')"
            >
              <option
                v-for="label in replacementGroups.otherLabels"
                :key="label.id"
                :value="label.id"
              >
                {{ cleanLabelTitle(label) }}
              </option>
            </optgroup>
          </select>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('LABEL_MGMT.DELETE.DIALOG.MOVE.SYNC_WARNING') }}
          </p>
        </div>
      </div>
    </Dialog>
  </SettingsLayout>
</template>
